import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Globe2, Smartphone, ShieldCheck } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import PaymentPrivacyDisclosure from '@/components/PaymentPrivacyDisclosure';
import { PaymentBadges } from '@/components/Pricing';
import { checkoutRequest, minuteAddonId, smsAddonId } from '@/lib/checkoutContract';
import { virtualNumberTelemetrySku } from '@/lib/productTelemetrySku';
const minuteOptions = [{
  label: 'None',
  price: 0
}, {
  label: '+30 min £1.50',
  price: 1.50
}, {
  label: '+1 hour £3.00',
  price: 3.00
}];
const smsOptions = [{
  label: 'None',
  price: 0
}, {
  label: '+50 SMS £1.50',
  price: 1.50
}, {
  label: '+100 SMS £3.00',
  price: 3.00
}];
const CustomSelect = ({
  value,
  onChange,
  options,
  isOpen,
  setIsOpen,
  className,
  focusBorderColor,
  dropdownTextClass = "text-xs"
}) => {
  const selectRef = useRef(null);
  const activeColorClass = focusBorderColor ? focusBorderColor.replace('border-', 'text-') : 'text-white';
  useEffect(() => {
    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [setIsOpen]);
  return <div className="relative w-full text-left" ref={selectRef}>
      <button type="button" onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(prev => !prev);
    }} className={`${className} flex items-center justify-between ${isOpen ? focusBorderColor : 'border-white/10'}`}>
        <span className="truncate">{options.find(o => (o.value !== undefined ? o.value : o) === value)?.label || value}</span>
        <svg className={`w-4 h-4 ml-2 shrink-0 transition-colors ${isOpen ? activeColorClass : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      <AnimatePresence>
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -5
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className={`absolute z-[100] w-full mt-1 bg-[#0f172a] border ${focusBorderColor || 'border-white/20'} rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar`}>
            {options.map(opt => {
          const optValue = opt.value !== undefined ? opt.value : opt;
          const optLabel = opt.label !== undefined ? opt.label : opt;
          return <div key={optValue} onClick={e => {
            e.stopPropagation();
            onChange(optValue);
            setIsOpen(false);
          }} className={`px-3 py-2 ${dropdownTextClass} text-white hover:bg-white/10 cursor-pointer transition-colors ${value === optValue ? 'bg-white/5 font-medium' : ''}`}>
                  {optLabel}
                </div>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const NumberCard = ({
  country,
  flag,
  basePrice,
  handleBuy,
  anchorId
}) => {
  const telemetrySku = virtualNumberTelemetrySku(country);
  const [extraMinutes, setExtraMinutes] = useState(0);
  const [extraSms, setExtraSms] = useState(0);
  const [usaConsent, setUsaConsent] = useState(false);
  const [userMobile, setUserMobile] = useState('');
  const [isExtraMinutesOpen, setIsExtraMinutesOpen] = useState(false);
  const [isExtraSmsOpen, setIsExtraSmsOpen] = useState(false);
  const getAddonCost = () => extraMinutes + extraSms;
  const calculateTotal = () => {
    return (basePrice + getAddonCost()).toFixed(2);
  };
  const getDynamicDesc = () => {
    const addonCost = getAddonCost();
    if (addonCost > 0) {
      return `Private number (+£${addonCost.toFixed(2)} extras), no contract`;
    }
    return `Private number, no contract`;
  };
  const usaRequirementsMissing = country === 'USA' && (!userMobile.trim() || !usaConsent);
  return <motion.div id={anchorId} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="scroll-mt-[150px] relative flex h-auto self-start flex-col overflow-visible rounded-xl border border-white/5 bg-[#1e293b] p-6 shadow-lg transition-all duration-300 group hover:-translate-y-1 hover:border-white/20 hover:shadow-2xl mb-8 md:mb-0">
      <div className="shrink-0 mb-4">
        <div className="mb-3 flex items-center gap-3">
          <span className="relative top-0.5 text-3xl text-[#38bdf8]" role="img" aria-label={`${country} flag`}>{flag}</span>
          <h3 className="text-xl font-bold text-white">{country} Virtual Number</h3>
        </div>
        <p className="text-xs font-medium text-[#38bdf8] mb-1">{getDynamicDesc()}</p>
        <p className="mb-3 text-[10.2px] font-semibold tracking-wide text-gray-200">30-Day Duration</p>
        <div className="flex items-baseline gap-1 mb-2">
          <span className="text-3xl font-black text-[#38bdf8]">£{calculateTotal()}</span>
        </div>
      </div>

      <div className="flex-grow overflow-visible pr-2 mb-4 flex flex-col gap-4">
        <ul className="space-y-2 shrink-0 pt-1">
           <li className="flex items-start gap-2 text-xs text-gray-300">
             <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#38bdf8]" />
             <span>Works with existing SIM/eSIM</span>
           </li>
           <li className="flex items-start gap-2 text-xs text-gray-300">
             <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#38bdf8]" />
             <span>Suitable for general communication and account verification where supported.</span>
           </li>
           <li className="flex items-start gap-2 text-xs text-gray-300">
             <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#38bdf8]" />
             <span>Stay reachable anywhere</span>
           </li>
        </ul>

        <div className="space-y-3 shrink-0 pb-1">
          <div className="p-3 rounded-xl bg-black/30 border border-white/5">
            <div className="mb-3">
              <span className="text-[10px] text-[#38bdf8] bg-[#38bdf8]/10 p-2 rounded-lg border border-[#38bdf8]/20 font-medium block text-center">
                Base plan does not include minutes/SMS
              </span>
            </div>

            <div className="grid grid-cols-1 gap-2.5">
              <div>
                <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider block mb-1">Add Extra Minutes (Optional)</label>
                <CustomSelect value={extraMinutes} onChange={setExtraMinutes} options={minuteOptions.map(opt => ({
                label: opt.label,
                value: opt.price
              }))} isOpen={isExtraMinutesOpen} setIsOpen={setIsExtraMinutesOpen} className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2 text-xs text-white transition-colors" focusBorderColor="border-[#38bdf8]" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider block mb-1">Add Extra SMS (Optional)</label>
                <CustomSelect value={extraSms} onChange={setExtraSms} options={smsOptions.map(opt => ({
                label: opt.label,
                value: opt.price
              }))} isOpen={isExtraSmsOpen} setIsOpen={setIsExtraSmsOpen} className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2 text-xs text-white transition-colors" focusBorderColor="border-[#38bdf8]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-auto shrink-0 pt-2 w-full flex flex-col gap-3">
        {country === 'USA' && <div className="space-y-3 rounded-xl border border-[#00ffc8]/30 bg-[#00ffc8]/[0.035] p-3 shadow-[0_0_20px_rgba(0,255,200,0.14)]">
            <div>
              <div className="mb-3 space-y-2 text-xs leading-relaxed text-gray-400">
                <p><em className="font-semibold text-[#38bdf8]">Why is this required?</em><br />US telecom requirements mandate verification to help prevent misuse. Your number is passed to the verification process only for this purpose and is not retained in the PhantomPath customer database.</p>
                <p><em className="font-semibold text-[#38bdf8]">Privacy by design:</em> In line with the data-minimisation and purpose-limitation principles of UK GDPR Article 5, we only process information necessary to provide and protect the service.</p>
              </div>
              <div className="mb-3 flex items-start gap-2.5 rounded-lg bg-white/[0.045] px-3 py-2.5">
                <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00ffc8]" aria-hidden="true" />
                <div className="text-xs leading-relaxed">
                  <p className="font-bold text-white">Global mobile numbers accepted</p>
                  <p className="mt-0.5 text-gray-300">This does not need to be a US number. Enter any valid mobile number using its international country code.</p>
                </div>
              </div>
              <label className="text-[10px] font-medium text-amber-400 uppercase tracking-wider block mb-1.5">Your mobile number (required for USA)</label>
              <input type="tel" value={userMobile} onChange={e => setUserMobile(e.target.value)} placeholder="+1 (555) 000-0000" className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={usaConsent} onChange={e => setUsaConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-500 shrink-0" />
              <span className="text-[10px] text-gray-300 leading-relaxed">
                I consent to receive transactional SMS from PhantomPathVPN regarding my service activation, access codes, and security alerts. Msg &amp; data rates may apply. Msg frequency varies. Reply STOP to opt-out, HELP for help. View <a href="/terms" className="text-[#38bdf8] underline">Terms</a> and <a href="/privacy-policy" className="text-[#38bdf8] underline">Privacy Policy</a>.
              </span>
            </label>
          </div>}
        <Button data-ga-event="begin_checkout" data-funnel-stage="product_purchase_clicked" data-product-name={`${country} Virtual Number`} data-product-id={telemetrySku} data-value={calculateTotal()} data-currency="GBP" disabled onClick={() => {
          if (usaRequirementsMissing) return;
          handleBuy(calculateTotal(), country + " Virtual Number", {
          includeVirtualNumber: true,
          virtualNumberCountry: country,
          minuteAddonId: minuteAddonId(extraMinutes),
          smsAddonId: smsAddonId(extraSms),
        }, telemetrySku);
        }} className={`w-full font-bold rounded-lg h-12 text-sm uppercase tracking-wider transition-all duration-300 glow-phantom glow-phantom-hover mt-auto ${usaRequirementsMissing ? 'bg-white/5 text-gray-600 border border-white/10 cursor-not-allowed' : 'bg-white/10 text-white border border-white/20 hover:bg-white hover:text-black'}`}>
          Get {country} Number
        </Button>
        <PaymentBadges methods={['stripe', 'paypal']} />
      </div>
    </motion.div>;
};
const VirtualNumbers = () => {
  const {
    toast
  } = useToast();
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const handleBuy = (total, name, selection, productSku) => {
    setCheckoutInfo({
      total,
      name,
      selection,
      productSku
    });
  };
  return <section id="virtual-numbers" className="scroll-mt-[100px] py-10 lg:py-14 px-4 bg-[#020617]">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-2.5 bg-[#38bdf8]/10 rounded-full mb-4">
            <Smartphone className="w-6 h-6 text-[#38bdf8]" />
          </div>
          <h2 className="mx-auto mb-3 max-w-4xl text-xl font-extrabold leading-tight tracking-tight text-white sm:text-2xl md:text-3xl">Virtual Numbers — a separate UK or USA number when you need one.</h2>
          <p className="mb-5 text-base font-semibold text-[#38bdf8]">No subscription. No commitment.</p>
      <div className="mx-auto mb-4 max-w-3xl px-1 py-1 text-left">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#38bdf8]" aria-hidden="true" />
              <div>
                <h3 className="mb-1 text-sm font-semibold text-[#38bdf8]">A separate number for supported calls and messaging</h3>
                <p className="text-sm leading-relaxed text-gray-400">Message content handled by PhantomPath is removed after delivery and is not retained as customer message history. Limited provider and operational records may still be processed where required to run the service.</p>
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
             £4.99 for the number for 30 days. Call minutes and SMS are available as optional add-ons. Some third-party services may reject virtual or VoIP numbers.
          </p>
        </div>

        <div className="mx-auto mb-8 flex max-w-4xl flex-col items-start gap-0 md:grid md:grid-cols-2 md:gap-6">
          <NumberCard anchorId="uk-virtual-number-30-day" country="UK" flag="🇬🇧" basePrice={4.99} handleBuy={handleBuy} />
          <NumberCard anchorId="usa-virtual-number-30-day" country="USA" flag="🇺🇸" basePrice={4.99} handleBuy={handleBuy} />
        </div>

        <div className="mx-auto mb-6 max-w-4xl">
          <PaymentPrivacyDisclosure showGeneral={false}>
            <p className="font-bold text-[#2dd4bf] uppercase tracking-wider mb-0.5">virtual numbers - UK &amp; usa</p>
            <p className="mb-1">Virtual numbers are provided through third-party telecommunications providers. Compatibility with verification services depends on the policies of each platform. SMS delivery is not guaranteed for all services.</p>
            <p>Telecom operations may be monitored for fraud, abuse, infrastructure security and legal compliance.</p>
            <p><strong className="text-red-400 font-medium">IMPORTANT:</strong> PhantomPath numbers DO NOT support emergency calling (999, 112, or 911). You must maintain an alternative means of contacting emergency services.</p>
            <p>18+ ONLY. Virtual-number service begins after successful payment. Cancellation and refund rights depend on the applicable law and service status; see the Refund Policy.</p>
          </PaymentPrivacyDisclosure>
        </div>

      </div>


      {checkoutInfo && <div className="public-checkout-overlay" style={{
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)',
      padding: '1rem'
    }} onClick={() => setCheckoutInfo(null)}>
          <div className="public-checkout-dialog" data-ga-location="virtual_number_payment_dialog" data-product-id={checkoutInfo.productSku} style={{
        background: '#111',
        border: '1px solid rgba(45,212,191,0.2)',
        borderRadius: '1rem',
        width: '100%',
        maxWidth: '24rem',
        overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>
            <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.25rem',
          borderBottom: '1px solid rgba(255,255,255,0.05)'
        }}>
              <div>
                <h3 style={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem'
            }}>{checkoutInfo.name}</h3>
                <p style={{
              color: '#2dd4bf',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginTop: '0.25rem'
            }}>{String.fromCharCode(163)}{checkoutInfo.total}</p>
              </div>
              <button onClick={() => setCheckoutInfo(null)} style={{
            width: '2rem',
            height: '2rem',
            borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            fontSize: '1.25rem'
          }}>{String.fromCharCode(10005)}</button>
            </div>
            <div style={{
          padding: '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
              <p style={{
            color: '#9ca3af',
            fontSize: '0.75rem',
            marginBottom: '0.5rem'
          }}>Choose your payment method:</p>
              <button data-ga-event="add_payment_info" data-product-name={checkoutInfo.name} data-value={checkoutInfo.total} data-currency="GBP" data-payment-method="stripe" onClick={() => {
            fetch('/api/checkout/dynamic', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(checkoutRequest(checkoutInfo, 'stripe'))
            }).then(r => r.json()).then(d => {
              if (d.url) window.location.href = d.url;
            });
          }} style={{
            width: '100%',
            height: '3rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            borderRadius: '0.75rem',
            cursor: 'pointer'
          }}>
                Pay with Card (Stripe)
              </button>
              <button data-ga-event="add_payment_info" data-product-name={checkoutInfo.name} data-value={checkoutInfo.total} data-currency="GBP" data-payment-method="paypal" onClick={() => {
            fetch('/api/checkout/dynamic', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(checkoutRequest(checkoutInfo, 'paypal'))
            }).then(r => r.json()).then(d => {
              if (d.url) window.location.href = d.url;
            });
          }} style={{
            width: '100%',
            height: '3rem',
            background: 'rgba(255,230,0,0.05)',
            border: '1px solid rgba(255,230,0,0.2)',
            color: '#FFE600',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            borderRadius: '0.75rem',
            cursor: 'pointer'
          }}>
                Pay with PayPal
              </button>
            </div>
          </div>
        </div>}

    </section>;
};
export default VirtualNumbers;
